import { useState, useRef } from "react";
import type { MetaFunction } from "@remix-run/cloudflare";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "@remix-run/react";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/prodi-food.module.css";
import { generateProdifoodPDF } from "~/lib/prodi-food-pdf";

export const meta: MetaFunction = () => [
  { title: "Webser-Food — Business Contract" },
  { name: "description", content: "Sign your Webser-Food contract. Simple, legal, and transparent." },
];

interface FormData {
  businessName: string;
  rut: string;
  address: string;
  businessPhone: string;
  supportPhone: string;
  channels: string[];
}

interface SignatureData {
  signature: string | null;
  accepted: boolean;
}

export default function ProdiFood() {
  const [step, setStep] = useState<"form" | "select" | "contract">("form");
  const [contractType, setContractType] = useState<"beta" | "full">("beta");
  const [splashDone, setSplashDone] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    businessName: "",
    rut: "",
    address: "",
    businessPhone: "",
    supportPhone: "",
    channels: [],
  });
  const [signatureData, setSignatureData] = useState<SignatureData>({
    signature: null,
    accepted: false,
  });

  const channelOptions = [
    "WhatsApp",
    "Instagram",
    "Telegram",
    "Email",
    "Phone",
  ];

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof FormData
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleChannelChange = (channel: string) => {
    setFormData({
      ...formData,
      channels: formData.channels.includes(channel)
        ? formData.channels.filter((c) => c !== channel)
        : [...formData.channels, channel],
    });
  };

  const handleContinue = () => {
    const { businessName, rut, address, businessPhone, supportPhone } =
      formData;
    if (!businessName || !rut || !address || !businessPhone || !supportPhone) {
      alert("Por favor completa todos los campos");
      return;
    }
    if (formData.channels.length === 0) {
      alert("Selecciona al menos un canal de atención");
      return;
    }
    setStep("select");
  };

  const handleSignatureCapture = (signatureDataUrl: string) => {
    setSignatureData({ ...signatureData, signature: signatureDataUrl });
  };

  const handleDownloadPDF = async () => {
    if (!signatureData.signature) {
      alert("Por favor firma el contrato");
      return;
    }
    if (!signatureData.accepted) {
      alert("Por favor acepta los términos y condiciones");
      return;
    }
    generateProdifoodPDF(formData, signatureData.signature);
  };

  return (
    <>
      <SplashScreen
        name="WEBSER-FOOD 🥑"
        tag="Restaurant Beta · Contract Signing"
        onComplete={() => setSplashDone(true)}
      />
      <div className={styles.root}>
        <div className={styles.header}>
        <Link to="/business-tools" className={styles.backBtn}>
          <ArrowLeft size={16} />
        </Link>
        <span className={styles.headerTag}>Webser-Food</span>
      </div>

      {step === "form" ? (
        <FormStep
          formData={formData}
          onFormChange={handleFormChange}
          onChannelChange={handleChannelChange}
          channelOptions={channelOptions}
          onContinue={handleContinue}
        />
      ) : step === "select" ? (
        <SelectStep
          onBack={() => setStep("form")}
          onSelectBeta={() => { setContractType("beta"); setStep("contract"); }}
          onSelectFull={() => { setContractType("full"); setStep("contract"); }}
        />
      ) : (
        <ContractStep
          formData={formData}
          contractType={contractType}
          signatureData={signatureData}
          onSignatureCapture={handleSignatureCapture}
          onAcceptedChange={(accepted) =>
            setSignatureData({ ...signatureData, accepted })
          }
          onDownloadPDF={handleDownloadPDF}
          onBack={() => setStep("select")}
        />
      )}
      <ProdiFooter />
      </div>
    </>
  );
}

function FormStep({
  formData,
  onFormChange,
  onChannelChange,
  channelOptions,
  onContinue,
}: {
  formData: FormData;
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>, field: keyof FormData) => void;
  onChannelChange: (channel: string) => void;
  channelOptions: string[];
  onContinue: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <h1 className={styles.title}>Webser-Food</h1>
        <p className={styles.brandSubtitle}>By Prodigium Dynamics</p>
        <p className={styles.subtitle}>
          Ingresa los datos de tu negocio para comenzar
        </p>
      </div>

      <div className={styles.formContent}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Nombre del negocio</label>
          <input
            type="text"
            className={styles.input}
            value={formData.businessName}
            onChange={(e) => onFormChange(e, "businessName")}
            placeholder="Mi Restaurante"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>RUT</label>
          <input
            type="text"
            className={styles.input}
            value={formData.rut}
            onChange={(e) => onFormChange(e, "rut")}
            placeholder="XX.XXX.XXX-X"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Dirección</label>
          <input
            type="text"
            className={styles.input}
            value={formData.address}
            onChange={(e) => onFormChange(e, "address")}
            placeholder="Calle Principal 123, Ciudad"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Teléfono del negocio</label>
          <input
            type="tel"
            className={styles.input}
            value={formData.businessPhone}
            onChange={(e) => onFormChange(e, "businessPhone")}
            placeholder="+56912345678"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Teléfono para soporte de clientes</label>
          <input
            type="tel"
            className={styles.input}
            value={formData.supportPhone}
            onChange={(e) => onFormChange(e, "supportPhone")}
            placeholder="+56912345678"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Medios de atención</label>
          <div className={styles.channelGrid}>
            {channelOptions.map((channel) => (
              <button
                key={channel}
                className={`${styles.channelBtn} ${
                  formData.channels.includes(channel)
                    ? styles.channelBtnActive
                    : ""
                }`}
                onClick={() => onChannelChange(channel)}
                type="button"
              >
                {channel}
              </button>
            ))}
          </div>
        </div>

        <button className={styles.ctaBtn} onClick={onContinue}>
          Continuar → Revisar Contrato
        </button>
      </div>
    </div>
  );
}

function ContractStep({
  formData,
  contractType,
  signatureData,
  onSignatureCapture,
  onAcceptedChange,
  onDownloadPDF,
  onBack,
}: {
  formData: FormData;
  contractType: "beta" | "full";
  signatureData: SignatureData;
  onSignatureCapture: (data: string) => void;
  onAcceptedChange: (accepted: boolean) => void;
  onDownloadPDF: () => void;
  onBack: () => void;
}) {
  const [isValidating, setIsValidating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const handleValidateAndDownload = async () => {
    if (!signatureData.signature) {
      alert("Por favor firma el contrato");
      return;
    }
    if (!signatureData.accepted) {
      alert("Por favor acepta los términos y condiciones");
      return;
    }

    onDownloadPDF();
    setIsSuccess(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsSuccess(false);
    navigate("/prodi-food/success");
  };
  return (
    <div className={styles.contractContainer}>
      <div className={styles.contractHeader}>
        <button className={styles.backSmallBtn} onClick={onBack}>
          ← Atrás
        </button>
        <h2 className={styles.contractTitle}>
          {contractType === "beta" ? "Términos y Condiciones" : "Contrato de Servicios"}
        </h2>
      </div>

      <div className={styles.contractContent}>
        {contractType === "beta" ? (
          <ContractContent />
        ) : (
          <ContractFullContent formData={formData} />
        )}
      </div>

      <div className={styles.signatureSection}>
        <h3 className={styles.signatureTitle}>Firma del Contrato</h3>
        <SignaturePad onCapture={onSignatureCapture} />
        {signatureData.signature && (
          <div className={styles.signaturePreview}>
            ✓ Firma registrada correctamente
          </div>
        )}
      </div>

      <div className={styles.acceptanceSection}>
        <label className={styles.checkboxLabel}>
          <div
            className={`${styles.checkboxBox} ${signatureData.accepted ? styles.checkboxBoxChecked : ""}`}
            onClick={() => onAcceptedChange(!signatureData.accepted)}
          >
            {signatureData.accepted && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path d="M1 5L4.5 8.5L11 1" stroke="#121214" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
          <span onClick={() => onAcceptedChange(!signatureData.accepted)}>
            {contractType === "beta"
              ? "Acepto los términos y condiciones de Webser-Food"
              : "Acepto el contrato de prestación de servicios con Webser Chile"}
          </span>
        </label>
      </div>

      <button
        className={styles.downloadBtn}
        onClick={handleValidateAndDownload}
        disabled={!signatureData.signature || !signatureData.accepted}
      >
        Descargar Contrato Firmado
      </button>

      {isSuccess && (
        <div className={styles.validatingOverlay}>
          <div className={styles.validatingModal}>
            <div className={styles.successIcon}>✓</div>
            <p className={styles.validatingText}>¡Contrato Validado!</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SignaturePad({ onCapture }: { onCapture: (data: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const draw = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // Captura el pointer: los eventos siguen llegando aunque el dedo salga del canvas
    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !lastPosRef.current) return;
    const pos = getPos(e);
    draw(lastPosRef.current, pos);
    lastPosRef.current = pos;
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
    lastPosRef.current = null;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  };

  const [isValidating, setIsValidating] = useState(false);

  const handleValidateSignature = async () => {
    if (!canvasRef.current) return;
    setIsValidating(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    onCapture(canvasRef.current.toDataURL("image/png"));
    setIsValidating(false);
  };

  return (
    <div className={styles.signaturePadContainer}>
      <canvas
        ref={canvasRef}
        className={styles.signatureCanvas}
        width={600}
        height={200}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <div className={styles.signaturePadActions}>
        <button className={styles.clearBtn} onClick={handleClear}>
          Limpiar
        </button>
        <button className={styles.captureBtn} onClick={handleValidateSignature} disabled={isValidating}>
          Validar Firma
        </button>
      </div>
      {isValidating && (
        <div className={styles.validatingOverlay}>
          <div className={styles.validatingModal}>
            <div className={styles.validatingSpinner} />
            <p className={styles.validatingText}>Validando</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SelectStep({
  onBack,
  onSelectBeta,
  onSelectFull,
}: {
  onBack: () => void;
  onSelectBeta: () => void;
  onSelectFull: () => void;
}) {
  return (
    <div className={styles.selectContainer}>
      <div className={styles.contractHeader}>
        <button className={styles.backSmallBtn} onClick={onBack}>← Atrás</button>
        <h2 className={styles.contractTitle}>Elige tu contrato</h2>
      </div>
      <p className={styles.selectHint}>
        Selecciona el tipo de contrato que corresponde a tu acuerdo con Webser-Food.
      </p>

      <div className={styles.selectCards}>
        <button className={styles.selectCard} onClick={onSelectBeta}>
          <div className={styles.selectCardTag}>Beta</div>
          <h3 className={styles.selectCardTitle}>Contrato Beta</h3>
          <p className={styles.selectCardDesc}>
            Para negocios en fase de prueba. Tienda online propia, soporte técnico directo,
            ajustes mensuales y Google Maps incluidos.
          </p>
          <div className={styles.selectCardPrice}>$25 USD — activación única</div>
          <span className={styles.selectCardCta}>Revisar contrato →</span>
        </button>

        <button className={styles.selectCard} onClick={onSelectFull}>
          <div className={`${styles.selectCardTag} ${styles.selectCardTagPro}`}>Pro</div>
          <h3 className={styles.selectCardTitle}>Contrato de Servicios</h3>
          <p className={styles.selectCardDesc}>
            Canal exclusivo de ventas, marcas virtuales (dark brands), marketing digital
            y operación completa bajo la plataforma Webser.
          </p>
          <div className={styles.selectCardPrice}>5% primer mes · 7% desde el segundo</div>
          <span className={styles.selectCardCta}>Revisar contrato →</span>
        </button>
      </div>
    </div>
  );
}

function ContractFullContent({ formData }: { formData: FormData }) {
  const today = new Date();
  const MONTHS = [
    "enero","febrero","marzo","abril","mayo","junio",
    "julio","agosto","septiembre","octubre","noviembre","diciembre",
  ];
  const dateStr = `${today.getDate()} de ${MONTHS[today.getMonth()]} de ${today.getFullYear()}`;

  return (
    <div className={styles.contractText}>
      <section className={styles.contractSection}>
        <h4>Contrato de Prestación de Servicios de Plataforma, Marketing y Generación de Marcas Gastronómicas</h4>
        <p className={styles.contractMeta}>En Santiago de Chile, a {dateStr}.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Las Partes</h5>
        <div className={styles.partiesBlock}>
          <div className={styles.partyRow}>
            <span className={styles.partyLabel}>El Prestador</span>
            <span className={styles.partyValue}>
              WEBSER CHILE, filial de PRODIGIUM DYNAMICS — RUT: (pendiente de asignación) — en adelante «el Prestador» o «Webser»
            </span>
          </div>
          <div className={styles.partyRow}>
            <span className={styles.partyLabel}>El Cliente</span>
            <span className={styles.partyValue}>
              {formData.businessName} — RUT: {formData.rut} — Domicilio: {formData.address} — en adelante «el Cliente» o «el Restaurante»
            </span>
          </div>
        </div>
        <p className={styles.contractMeta}>Quienes acuerdan celebrar el siguiente contrato:</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Primero: Antecedentes</h5>
        <p>1.1. El Prestador opera «Webser-Food», una plataforma tecnológica de toma y gestión de pedidos en línea, y presta servicios de marketing digital y de creación y operación de marcas gastronómicas virtuales (dark/cloud brands) sobre la cocina de establecimientos de terceros.</p>
        <p>1.2. El Cliente opera una cocina y desea incrementar su volumen de ventas mediante el canal, las marcas y la operación de marketing del Prestador.</p>
        <p>1.3. Las Partes declaran que este contrato constituye un acuerdo de prestación de servicios entre empresas independientes, sin que genere vínculo laboral, sociedad de hecho ni mandato representativo entre ellas.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Segundo: Definiciones</h5>
        <ul>
          <li><strong>«Canal Webser-Food»:</strong> la plataforma, sitios, enlaces, códigos QR, landings y todo punto de toma de pedidos operado por el Prestador a través del cual los clientes finales cursan pedidos al Restaurante.</li>
          <li><strong>«Marcas Virtuales»:</strong> las marcas gastronómicas creadas, diseñadas y/o operadas por Webser que se elaboran físicamente en la cocina del Cliente, incluyendo cualquier marca futura desarrollada bajo este contrato.</li>
          <li><strong>«Venta Bruta»:</strong> el valor total pagado por el cliente final por los productos cursados a través del Canal Webser-Food, antes de descontar costos de despacho de terceros, excluyendo propinas voluntarias.</li>
          <li><strong>«Comisión»:</strong> la contraprestación del Prestador, definida en la cláusula Cuarta.</li>
        </ul>
      </section>

      <section className={styles.contractSection}>
        <h5>Tercero: Objeto y Canal Único de Venta</h5>
        <p>3.1. El Prestador prestará al Cliente: (i) provisión y operación del Canal Webser-Food; (ii) diseño, lanzamiento y operación de Marcas Virtuales sobre la cocina del Cliente; y (iii) ejecución de campañas de marketing y captación de demanda.</p>
        <p>3.2. <strong>Canal único.</strong> El Cliente acepta que la totalidad de las ventas generadas por la operación de marketing y por las Marcas Virtuales se cursarán exclusivamente a través del Canal Webser-Food. Toda venta atribuible a dicho canal queda sujeta a la Comisión.</p>
        <p>3.3. <strong>Integridad del canal.</strong> El Cliente se obliga a no desviar, ocultar ni cursar fuera del Canal Webser-Food ventas correspondientes a las Marcas Virtuales o campañas del Prestador. En caso de detectarse elusión, el Prestador podrá cerrar de forma inmediata los canales sin aviso previo.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Cuarto: Comisión</h5>
        <table className={styles.contractTable}>
          <tbody>
            <tr><td><strong>Período</strong></td><td><strong>Comisión</strong></td></tr>
            <tr><td>Primer mes (adquisición)</td><td>5% de la Venta Bruta</td></tr>
            <tr><td>Desde el segundo mes en adelante</td><td>7% de la Venta Bruta</td></tr>
          </tbody>
        </table>
        <p>4.2. La Comisión aplica a todas las Marcas Virtuales y a la marca propia del Cliente.</p>
        <p>4.3. La Comisión es la única retribución del Prestador. Los costos de insumos y elaboración son de cargo exclusivo del Cliente.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Quinto: Liquidación y Pago</h5>
        <p>5.1. El Prestador emitirá liquidaciones periódicas de ventas y Comisión, dentro de los días acordados al cierre de cada período.</p>
        <p>5.2. El Cliente pagará la Comisión dentro de los plazos acordados desde la recepción de la liquidación, mediante transferencia a la cuenta indicada por el Prestador.</p>
        <p>5.3. Las Partes podrán acordar que el Prestador retenga y descuente la Comisión directamente de los montos recaudados a través del Canal Webser-Food, liquidando al Cliente el saldo neto.</p>
        <p>5.4. <strong>Prevalencia del registro.</strong> El registro de ventas del Canal Webser-Food constituye la fuente oficial para determinar la Venta Bruta y la Comisión devengada. En caso de discrepancia, prevalecerá el registro del Canal, salvo error manifiesto acreditado por el Cliente.</p>
        <p>5.5. <strong>Protocolo de corte por descuadre.</strong> Si el Prestador detecta cualquier diferencia entre la Comisión devengada y lo pagado, podrá suspender o cerrar de forma inmediata el Canal y los canales de atención, sin aviso previo, hasta el pago íntegro de lo adeudado.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Sexto: Exclusividad de Generación de Marcas</h5>
        <p>6.1. Durante la vigencia, Webser Chile será el único y exclusivo proveedor de generación y operación de Marcas Virtuales sobre la cocina del Cliente.</p>
        <p>6.2. El Cliente no podrá contratar con plataformas o agencias servicios equivalentes de generación de marcas mientras el contrato esté vigente.</p>
        <p>6.3. El incumplimiento de esta cláusula facultará al Prestador para terminar el contrato inmediatamente y exigir la indemnización correspondiente.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Séptimo: Propiedad de las Marcas Virtuales</h5>
        <p>7.1. Las Marcas Virtuales —nombres, logotipos, identidad visual, recetarios, cuentas y activos digitales— son y permanecerán de propiedad exclusiva de Webser Chile.</p>
        <p>7.2. Webser concede al Cliente una licencia de uso limitada, no exclusiva y revocable sobre las Marcas Virtuales, vigente solo mientras dure este contrato.</p>
        <p>7.3. Terminado el contrato por cualquier causa, cesará la licencia y el Cliente deberá abstenerse de usar las Marcas Virtuales o su identidad visual.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Octavo: Marketing, Contenido y Publicidad</h5>
        <p>8.1. <strong>Contenido orgánico.</strong> La creación y publicación de contenido en redes sociales es producida por el Prestador, a su criterio y como parte de sus servicios, sin costo adicional.</p>
        <p>8.2. <strong>Publicidad pagada.</strong> Toda inversión en publicidad pagada será de cargo exclusivo del Cliente. El Prestador podrá gestionar y optimizar la pauta, pero el presupuesto corresponde al Cliente, previo acuerdo del monto a invertir.</p>
        <p>8.3. <strong>Precios orientados a volumen.</strong> El Cliente se obliga a mantener los precios de venta dentro de los rangos acordados con el Prestador. Todo cambio de precios requerirá acuerdo previo entre las Partes.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Noveno: Obligaciones del Cliente</h5>
        <ul>
          <li>Elaborar los pedidos con la calidad, presentación y estándares acordados, dentro de los tiempos comprometidos.</li>
          <li>Disponer, gestionar y costear el reparto a domicilio y los repartidores necesarios para la entrega de pedidos.</li>
          <li>Operar, gestionar y costear la atención al público y la postventa en todos los canales de contacto con el cliente final.</li>
          <li>Financiar la inversión en publicidad pagada conforme a la cláusula Octava.</li>
          <li>Mantener las autorizaciones sanitarias, patentes y permisos exigidos por la normativa vigente.</li>
          <li>Cursar la totalidad de las ventas de las Marcas Virtuales y campañas a través del Canal Webser-Food, sin elusión de Comisión.</li>
          <li>Respetar la exclusividad y la propiedad de marcas pactadas en las cláusulas Sexta y Séptima.</li>
          <li>Pagar oportunamente la Comisión conforme a la cláusula Quinta.</li>
        </ul>
      </section>

      <section className={styles.contractSection}>
        <h5>Décimo: Estándar de Servicio y Auditoría</h5>
        <p>10.1. El Cliente se obliga a prestar al cliente final una atención de excelencia en todos los canales y en cada etapa del pedido. Este estándar es condición esencial del contrato.</p>
        <p>10.2. El Prestador podrá auditar, en cualquier momento, la calidad de la atención prestada por el Cliente (incluyendo pedidos de prueba y revisión de conversaciones).</p>
        <p>10.3. El incumplimiento reiterado del estándar facultará al Prestador para terminar el contrato conforme a la cláusula Decimoquinta.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Undécimo: Obligaciones del Prestador</h5>
        <ul>
          <li>Mantener operativo el Canal Webser-Food y entregar liquidaciones claras y oportunas.</li>
          <li>Diseñar y operar las Marcas Virtuales y ejecutar las acciones de marketing acordadas.</li>
          <li>Resguardar los datos personales de los clientes finales conforme a la Ley N.º 19.628 y normativa aplicable.</li>
        </ul>
      </section>

      <section className={styles.contractSection}>
        <h5>Duodécimo: Uso como Caso de Estudio</h5>
        <p>El Cliente autoriza al Prestador a utilizar los resultados agregados de la operación como caso de estudio público con fines comerciales y de difusión de Webser-Food y Prodigium Dynamics, pudiendo o no identificar al Restaurante según se acuerde por escrito.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Decimotercero: Confidencialidad</h5>
        <p>Las Partes mantendrán reserva sobre la información comercial, técnica y operativa no pública intercambiada con ocasión de este contrato. Esta obligación subsistirá por 2 años tras su término.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Decimocuarto: Vigencia y Renovación</h5>
        <p>El contrato regirá por el plazo inicial acordado entre las Partes, renovable automáticamente por períodos iguales salvo aviso escrito con 30 días corridos de anticipación al vencimiento.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Decimoquinto: Término Anticipado</h5>
        <p>15.1. Cualquiera de las Partes podrá poner término al contrato con aviso escrito de 30 días corridos de anticipación, sin derecho a indemnización por el solo hecho del término.</p>
        <p>15.2. El Prestador podrá terminar el contrato de inmediato, sin aviso ni indemnización, en caso de: (i) incumplimiento de las cláusulas Quinta, Sexta o Séptima; (ii) elusión de Comisión o desvío de ventas; (iii) descuadre no subsanado conforme a la cláusula 5.5; o (iv) incumplimiento del estándar de servicio.</p>
        <p>15.3. Terminado el contrato, cesa la licencia sobre las Marcas Virtuales. El Cliente conserva únicamente su marca propia y deberá pagar las Comisiones devengadas pendientes.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Decimosexto: Responsabilidad</h5>
        <p>El Cliente es el único responsable de la calidad, inocuidad y cumplimiento sanitario de los productos que elabora. El Prestador no responde por hechos derivados de la elaboración de alimentos por parte del Cliente.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>Decimoséptimo: Ley Aplicable</h5>
        <p>Este contrato se rige por las leyes de la República de Chile. Toda controversia será sometida a los Tribunales Ordinarios de Justicia de la Región Metropolitana.</p>
      </section>

      <section className={styles.contractSection}>
        <p className={styles.contractFooter}>
          WEBSER CHILE, filial de PRODIGIUM DYNAMICS — Contrato de Servicios
        </p>
      </section>
    </div>
  );
}

function ProdiFooter() {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerTop}>
        <div className={styles.footerBrandBlock}>
          <span className={styles.footerBrandName}>WEBSER-FOOD</span>
          <span className={styles.footerBrandBy}>by Prodigium Dynamics</span>
        </div>
        <div className={styles.footerTrustRow}>
          <div className={styles.footerTrustBadge}>
            <span className={styles.footerTrustDot} />
            Contrato con validez legal
          </div>
          <div className={styles.footerTrustBadge}>
            <span className={styles.footerTrustDot} />
            Datos protegidos
          </div>
          <div className={styles.footerTrustBadge}>
            <span className={styles.footerTrustDot} />
            Soporte 24/7
          </div>
        </div>
      </div>

      <div className={styles.footerDivider} />

      <div className={styles.footerMid}>
        <div className={styles.footerColumn}>
          <span className={styles.footerColumnTitle}>Contacto</span>
          <a href="mailto:global@prodigiumdynamics.space" className={styles.footerLink}>
            global@prodigiumdynamics.space
          </a>
          <a href="/contact" className={styles.footerLink}>Formulario de contacto</a>
        </div>
        <div className={styles.footerColumn}>
          <span className={styles.footerColumnTitle}>Plataforma</span>
          <a href="/business-tools" className={styles.footerLink}>Business Tools</a>
          <a href="/about" className={styles.footerLink}>Acerca de</a>
        </div>
        <div className={styles.footerColumn}>
          <span className={styles.footerColumnTitle}>Legal</span>
          <span className={styles.footerMeta}>Versión Beta — 2026</span>
          <span className={styles.footerMeta}>Contrato vinculante</span>
        </div>
      </div>

      <div className={styles.footerDivider} />

      <div className={styles.footerBottom}>
        <span className={styles.footerCopy}>© 2026 Prodigium Dynamics. Todos los derechos reservados.</span>
        <span className={styles.footerTagline}>Webser-Food es un servicio de Prodigium Dynamics.</span>
      </div>
    </footer>
  );
}

function ContractContent() {
  return (
    <div className={styles.contractText}>
      <section className={styles.contractSection}>
        <h4>Términos y Condiciones de Uso — Fase Beta</h4>
        <p className={styles.contractSubtitle}>
          Prodigium Dynamics — Webser-Food
        </p>
        <p className={styles.contractMeta}>
          Vigente para: clientes beta de la plataforma Prodi-Food
        </p>
      </section>

      <section className={styles.contractSection}>
        <h5>1. Qué incluye tu tienda</h5>
        <p>Al activar tu cuenta beta recibes:</p>
        <ul>
          <li>Tu tienda online con link propio, sistema de pedidos y carrito funcional.</li>
          <li>Soporte técnico directo durante toda la beta (vía WhatsApp/email en horario extendido).</li>
          <li>Inscripción en Google Maps: si tu negocio no aparece o tiene datos desactualizados, nosotros lo gestionamos.</li>
          <li>Capacitación básica: te enseñamos a cargar productos, cambiar precios y revisar pedidos.</li>
          <li>Hasta 10 ajustes mensuales sin costo extra: promociones, banners, cambios de texto, fotos de productos o precios.</li>
          <li>Web como PWA (tu negocio estilo app)</li>
        </ul>
      </section>

      <section className={styles.contractSection}>
        <h5>2. Qué NO incluye (y cómo pedirlo)</h5>
        <p>Lo siguiente no está cubierto en la beta y se cotiza aparte:</p>
        <ul>
          <li>Personalización fuerte de la interfaz: cambios de colores, tipografías, estructura de la página, funciones nuevas o integraciones con sistemas externos.</li>
          <li>Diseño gráfico avanzado: logos, fotografía profesional, videos promocionales.</li>
          <li>Desarrollo a medida: cualquier feature que no exista actualmente en la plataforma.</li>
        </ul>
        <p>Si necesitas algo de esto, hablamos y te damos precio antes de tocar código.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>3. Tus responsabilidades vs las nuestras</h5>
        <table className={styles.contractTable}>
          <tbody>
            <tr>
              <td><strong>Tú (el negocio)</strong></td>
              <td><strong>Nosotros (Prodigium)</strong></td>
            </tr>
            <tr>
              <td>Atender a tus clientes finales. Resolver dudas de pedidos, tiempos de entrega, calidad de comida y pagos.</td>
              <td>Mantener la plataforma online, estable y segura.</td>
            </tr>
            <tr>
              <td>Cargar correctamente tu menú, precios y horarios.</td>
              <td>Darte soporte técnico cuando algo de la plataforma no funcione.</td>
            </tr>
            <tr>
              <td>Usar la tienda para fines legales.</td>
              <td>Revisar y reparar bugs que afecten el funcionamiento del sistema.</td>
            </tr>
          </tbody>
        </table>
        <p>Regla simple: si tu cliente no recibe su pedido, es tu área. Si el botón de "pedir" no funciona, es la nuestra. Siempre buscaremos solucionar fallas técnicas que comprometan la operación, pero no somos responsables por la gestión del negocio ni por conflictos entre tú y tus clientes.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>4. Tu dominio y tus datos</h5>
        <p><strong>Datos de tus clientes:</strong></p>
        <p>Toda la información que ingresen los compradores en tu tienda (nombres, teléfonos, direcciones, historial de pedidos) es propiedad exclusiva de tu negocio. Nosotros solo la almacenamos en nuestros servidores como si fuéramos una nube. Nunca la venderemos, compartiremos ni usaremos para otros fines. Si algún día dejas la plataforma, te entregamos tus datos o los eliminamos por completo, como tú prefieras.</p>
        <p><strong>Tu dominio:</strong></p>
        <p>El dominio de tu tienda (ej. tunegocio.com) es tuyo. Mientras permanezcas en Prodigium, nosotros cubrimos su renovación anual. Si decides salir de la plataforma, puedes llevarte el dominio contigo; en ese caso, deberás hacerse cargo de los costos de transferencia y renovación futuros.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>5. Pago en fase beta</h5>
        <p>La beta tiene un costo único de 25 USD. No hay mensualidades ni comisiones por venta durante esta fase.</p>
        <ul>
          <li>Ese pago cubre la activación de tu tienda, la configuración del dominio y tu ingreso al grupo de control.</li>
          <li>Como beta tester, tendrás acceso a todas las actualizaciones que le hagamos a la plataforma.</li>
          <li>Tu rol es ser nuestro termómetro directo: nos cuentas qué funciona, qué no y qué necesitas. Eso es lo que pagas con los 25: tu entrada al grupo cerrado.</li>
        </ul>
      </section>

      <section className={styles.contractSection}>
        <h5>6. Cuándo suspendemos el servicio</h5>
        <p>Nuestro objetivo principal es que el canal de tu restaurante esté siempre abierto para tus clientes y comensales. Por eso, garantizamos el funcionamiento de la plataforma las 24 horas del día, los 7 días de la semana, y cualquier falla técnica será atendida con prioridad máxima para restaurar el servicio lo antes posible.</p>
        <p>Sin embargo, podemos suspender o cerrar tu tienda de inmediato si detectamos:</p>
        <ul>
          <li>Venta de productos ilegales, ilícitos o prohibidos.</li>
          <li>Uso de la plataforma para estafar, phishing o cualquier actividad delictual.</li>
          <li>Intentos de dañar el sistema (hackeo, sobrecarga, ingeniería inversa).</li>
          <li>Incumplimiento grave de estos términos.</li>
        </ul>
        <p>En esos casos no hay devolución del pago de activación.</p>
      </section>

      <section className={styles.contractSection}>
        <h5>7. Fallas técnicas: qué cubrimos y qué no</h5>
        <p>Hacemos todo lo posible para que la plataforma esté disponible 24/7, pero somos humanos y la tecnología falla.</p>
        <ul>
          <li><strong>Nuestra responsabilidad:</strong> reparar bugs, caídas del servidor o errores de código que impidan el funcionamiento normal.</li>
          <li><strong>No nuestra responsabilidad:</strong> pérdida de ventas por un corte de internet en tu zona, que un cliente no pague, que se agote tu stock, o que una red social donde anuncias tu tienda deje de funcionar.</li>
        </ul>
        <p><strong>Límite de responsabilidad:</strong> en ningún caso Prodigium será responsable por ganancias perdidas, daños indirectos o perjuicios que excedan el monto que pagaste por activación (25 USD).</p>
      </section>

      <section className={styles.contractSection}>
        <h5>8. Reglas de la beta y el futuro</h5>
        <ul>
          <li>Esta fase beta está limitada a 10 negocios. No habrá cambios de precio ni cobros sorpresa durante la beta.</li>
          <li>Cuando la plataforma salga de beta, los beta testers mantendrán condiciones preferenciales.</li>
          <li>Como gesto de que la relación sigue viva, una vez al año nos gustaría que nos invitaras a un platillo de tu menú (o su equivalente). No es un pago obligatorio: es un símbolo de que seguimos creciendo juntos.</li>
        </ul>
        <p>No hay letra chica, pero sí reglas claras para que ambos (plataforma y restaurantes) ganemos.</p>
      </section>

      <section className={styles.contractSection}>
        <p className={styles.contractFooter}>
          Prodigium Dynamics — Webser-Food.
        </p>
      </section>
    </div>
  );
}
